// SettingsModal.jsx - Sovereign Left-Sidebar Command Hub for MeMark
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

import TabGeneral from './settings/TabGeneral';
import TabFeatures from './settings/TabFeatures';
import TabBackground from './settings/TabBackground';
import TabColors from './settings/TabColors';
import TabAudio from './settings/TabAudio';
import TabAI from './settings/TabAI';
import TabVault from './settings/TabVault';
import TabAdmin from './settings/TabAdmin';

import {
  buildThemeColors,
  extractFontOverrides,
  getModalTheme,
} from './settings/settingsUtils';
import { playSound } from '../lib/soundEffects';

export { ANIMATED_BACKGROUNDS, FONT_TARGETS, THEME_PRESETS } from './settings/settingsConstants';
export { buildThemeColors, extractFontOverrides } from './settings/settingsUtils';

const {
  FiX, FiDroplet, FiFilm, FiHome, FiVolume2, FiCpu, FiShield, FiSliders, FiZap,
  FiCheck, FiExternalLink, FiSettings, FiLogOut
} = FiIcons;

const TABS = [
  { id: 'general', label: 'General & Backup', icon: FiHome, desc: 'Workspace title, Netscape HTML & JSON exports' },
  { id: 'features', label: 'Features & Modules', icon: FiZap, desc: 'Enable or disable extra tools (AI, Pomodoro, CLI, etc.)' },
  { id: 'colors', label: 'Themes & Palette', icon: FiDroplet, desc: '10 curated new-age themes, dark/light modes & accents' },
  { id: 'background', label: 'Atmosphere FX', icon: FiFilm, desc: 'Minimal Zen, Constellation stars, Aurora, Nebula' },
  { id: 'ai', label: 'AI & BYOK Gateway', icon: FiCpu, desc: 'Bring your own API key, model selection, local storage' },
  { id: 'vault', label: 'Vault Crypto', icon: FiShield, desc: 'Client-side AES-GCM-256 encrypted passwords & PIN' },
  { id: 'audio', label: 'Audio Synthesizer', icon: FiVolume2, desc: 'Web Audio procedural clicks, chimes & sound profiles' },
];

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function normalizeHex(v) {
  return (v || '').trim().toUpperCase();
}

function resolveAccent(patch, settings) {
  const candidate =
    patch?.customAccent ||
    patch?.accent ||
    settings.themeColors?.accent ||
    '#10B981';

  const normalized = normalizeHex(candidate);
  return HEX_RE.test(normalized) ? normalized : '#10B981';
}

const SettingsModal = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
  data = {},
  onImportBookmarks,
  initialTab = 'general',
  user,
  isAdmin = false,
  onSignOut,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (isOpen) {
      playSound('click');
      if (initialTab) setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Keyboard shortcut ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const isDark = settings.isDark !== false;
  const accent = (settings.themeColors?.accent || '#10B981').toUpperCase();
  const theme = getModalTheme(isDark);

  const applyChange = (patch) => {
    const dark = patch.isDark !== undefined ? patch.isDark : isDark;
    const resolvedAccent = resolveAccent(patch, settings);
    const existingOverrides = extractFontOverrides(settings.themeColors);

    const next = {
      ...settings,
      ...patch,
      isDark: dark,
      themeColors: patch.themeColors || buildThemeColors(resolvedAccent, dark, existingOverrides),
    };

    onSettingsChange(next);
  };

  const handleFontColorsApply = (fontColors, isCustom) => {
    const dark = settings.isDark !== false;
    const existingOverrides = extractFontOverrides(settings.themeColors);

    const incomingAccent = normalizeHex(fontColors?.accent || settings.themeColors?.accent || '#10B981');
    const safeAccent = HEX_RE.test(incomingAccent) ? incomingAccent : '#10B981';

    const mergedOverrides = isCustom
      ? { ...existingOverrides, ...fontColors, _customFonts: true }
      : {};

    onSettingsChange({
      ...settings,
      theme: 'custom',
      themeColors: buildThemeColors(safeAccent, dark, mergedOverrides),
    });
  };

  const isUserAdmin = isAdmin || user?.email === 'y.yang.priv@gmail.com' || user?.role === 'admin';
  const availableTabs = [
    ...TABS,
    ...(isUserAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin Hub',
            icon: FiShield,
            desc: 'Manage user access, approvals & reverse proxy gateway',
            isAdminTab: true,
          },
        ]
      : []),
  ];

  const activeTabData = availableTabs.find((t) => t.id === activeTab) || availableTabs[0];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { playSound('click'); onClose(); }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Container: Spacious Modern Two-Column Layout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="relative z-10 w-full max-w-5xl h-[88vh] max-h-[780px] rounded-3xl border shadow-2xl flex flex-col md:flex-row overflow-hidden"
            style={{
              backgroundColor: isDark ? '#080C0A' : '#FFFFFF',
              borderColor: isDark ? 'rgba(16, 185, 129, 0.28)' : '#E2E8F0',
              boxShadow: isDark
                ? '0 25px 70px rgba(0,0,0,0.9), 0 0 50px rgba(16, 185, 129, 0.12)'
                : '0 25px 70px rgba(0,0,0,0.15)',
            }}
          >
            {/* Holographic Top Laser Accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500/10 via-emerald-400 to-emerald-500/10 z-20" />

            {/* ── LEFT SIDEBAR NAVIGATION (Desktop) ───────────────────────── */}
            <aside
              className="hidden md:flex flex-col w-64 shrink-0 border-r select-none"
              style={{
                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                backgroundColor: isDark ? '#050807' : '#F8FAFC',
              }}
            >
              {/* Header Title on the Left */}
              <div className="p-5 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0' }}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm shrink-0"
                    style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.15)',
                      border: '1px solid rgba(16, 185, 129, 0.35)',
                    }}
                  >
                    <SafeIcon icon={FiSliders} className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold tracking-tight flex items-center gap-1.5"
                      style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>
                      <span>MeMark Hub</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        CLOUD SYNCED
                      </span>
                    </h2>
                    <p className="text-[11px] font-mono" style={{ color: isDark ? '#A1A1AA' : '#64748B' }}>Preferences &amp; Labs</p>
                  </div>
                </div>
              </div>

              {/* Vertical Navigation Tabs */}
              <div className="p-3 flex-1 overflow-y-auto space-y-1">
                {availableTabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  const isAdminTab = tab.id === 'admin';
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => { playSound('click'); setActiveTab(tab.id); }}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold text-left transition-all cursor-pointer ${
                        isActive
                          ? isAdminTab
                            ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.15)]'
                            : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                          : isAdminTab
                          ? 'text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10 border border-amber-500/20'
                          : isDark
                          ? 'text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/70 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3 truncate">
                        <SafeIcon
                          icon={tab.icon}
                          className={`w-4 h-4 shrink-0 transition-colors ${
                            isActive
                              ? isAdminTab ? 'text-amber-400' : 'text-emerald-400'
                              : isAdminTab ? 'text-amber-400' : isDark ? 'text-zinc-500' : 'text-slate-500'
                          }`}
                        />
                        <span className="truncate">{tab.label}</span>
                      </div>
                      {isAdminTab && (
                        <span className="text-[9px] font-mono px-1.5 py-0.2 rounded font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          PRO
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Sidebar Footer Status & Sign Out */}
              <div
                className="p-3 border-t text-[11px] font-mono flex flex-col gap-2"
                style={{
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
                  color: isDark ? '#6B7280' : '#64748B',
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span>{user?.email === 'y.yang.priv@gmail.com' ? '👑 Admin Mode' : 'Cloud Sync: Active • BYOK: Local'}</span>
                  </div>
                  <span className="text-[10px]">v2.5</span>
                </div>
                {onSignOut && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Sign out of MeMark?')) {
                        onClose();
                        onSignOut();
                      }
                    }}
                    className="w-full py-1.5 px-2.5 rounded-lg text-xs font-semibold text-red-400 hover:text-white bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                  >
                    <SafeIcon icon={FiLogOut} className="w-3.5 h-3.5" />
                    <span>Sign Out</span>
                  </button>
                )}
              </div>
            </aside>

            {/* ── MOBILE TOP NAVIGATION BAR (< md) ───────────────────────── */}
            <div
              className="flex md:hidden flex-col border-b shrink-0 select-none"
              style={{
                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                backgroundColor: isDark ? '#050807' : '#F8FAFC',
              }}
            >
              {/* Mobile Header Row */}
              <div className="flex items-center justify-between px-4 py-3 border-b"
                style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0' }}>
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                  >
                    <SafeIcon icon={FiSliders} className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-bold" style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>
                    MeMark Settings
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {onSignOut && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Sign out of MeMark?')) {
                          onClose();
                          onSignOut();
                        }
                      }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:text-white border border-red-500/20 bg-red-500/10 cursor-pointer"
                      title="Sign Out"
                    >
                      <SafeIcon icon={FiLogOut} className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { playSound('click'); onClose(); }}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${
                      isDark
                        ? 'text-zinc-400 hover:text-white border-white/10 hover:bg-white/5'
                        : 'text-slate-600 hover:text-slate-900 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Horizontal Scrollable Tabs */}
              <div
                className="flex items-center gap-1.5 p-2 overflow-x-auto scrollbar-none no-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                data-no-scrollbar="true"
              >
                {availableTabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  const isAdminTab = tab.id === 'admin';
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => { playSound('click'); setActiveTab(tab.id); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                        isActive
                          ? isAdminTab
                            ? 'bg-amber-500/25 text-amber-300 border border-amber-500/40'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                          : isAdminTab
                          ? 'text-amber-400 border border-amber-500/20'
                          : isDark
                          ? 'text-zinc-400 hover:text-white border border-transparent'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 border border-transparent'
                      }`}
                    >
                      <SafeIcon icon={tab.icon} className={`w-3.5 h-3.5 ${isAdminTab ? 'text-amber-400' : ''}`} />
                      <span>{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── RIGHT MAIN CONTENT AREA ─────────────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              {/* Desktop Header */}
              <div
                className="hidden md:flex items-center justify-between px-8 py-4.5 border-b shrink-0"
                style={{
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
                  backgroundColor: isDark ? '#070A09' : '#FFFFFF',
                }}
              >
                <div>
                  <h3 className="text-base font-bold tracking-tight" style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>
                    {activeTabData.label}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: isDark ? '#A1A1AA' : '#64748B' }}>{activeTabData.desc}</p>
                </div>

                <button
                  type="button"
                  onClick={() => { playSound('click'); onClose(); }}
                  className={`w-8 h-8 rounded-xl border flex items-center justify-center transition-all cursor-pointer ${
                    isDark
                      ? 'text-zinc-400 hover:text-white hover:border-zinc-500 bg-white/[0.03] border-white/[0.08]'
                      : 'text-slate-600 hover:text-slate-900 hover:border-slate-400 bg-slate-100 border-slate-300'
                  }`}
                  title="Close settings (ESC)"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>

              {/* Scrollable Tab Body with Abundant Space */}
              <div className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto space-y-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.16 }}
                  >
                    {activeTab === 'general' && (
                      <TabGeneral
                        settings={settings}
                        onSettingsChange={onSettingsChange}
                        theme={theme}
                        data={data}
                        onImportBookmarks={onImportBookmarks}
                      />
                    )}

                    {activeTab === 'features' && (
                      <TabFeatures
                        theme={theme}
                        accent={accent}
                      />
                    )}

                    {activeTab === 'colors' && (
                      <TabColors
                        settings={settings}
                        onApplyChange={applyChange}
                        onFontColorsApply={handleFontColorsApply}
                        theme={theme}
                        isDark={isDark}
                      />
                    )}

                    {activeTab === 'background' && (
                      <TabBackground
                        settings={settings}
                        onApplyChange={applyChange}
                        theme={theme}
                        isDark={isDark}
                      />
                    )}

                    {activeTab === 'ai' && (
                      <TabAI
                        settings={settings}
                        onSettingsChange={onSettingsChange}
                        theme={theme}
                        accent={accent}
                      />
                    )}

                    {activeTab === 'vault' && (
                      <TabVault
                        theme={theme}
                        accent={accent}
                      />
                    )}

                    {activeTab === 'audio' && (
                      <TabAudio
                        theme={theme}
                        accent={accent}
                      />
                    )}

                    {activeTab === 'admin' && (
                      <TabAdmin
                        theme={theme}
                        accent={accent}
                        user={user}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer Bar */}
              <div
                className="flex items-center justify-between px-6 py-3.5 border-t shrink-0 text-xs text-zinc-500 font-mono"
                style={{
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
                  backgroundColor: isDark ? '#050807' : '#F8FAFC',
                }}
              >
                <span className="hidden sm:inline">
                  Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-zinc-300 font-mono">ESC</kbd> to exit
                </span>
                <button
                  type="button"
                  onClick={() => { playSound('click'); onClose(); }}
                  className="px-5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] cursor-pointer ml-auto"
                >
                  Save &amp; Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SettingsModal;